// Voice control (Kareem, 4 Nov: "control the whole system and anything on screen by voice"): voice mode with the app's
// controls. Say where to go ("open the steering committee", "show my reminders"), find things ("search for the data
// dictionary"), filter a space's list ("my tasks in Data Readiness"), scroll, go back, "what am I looking at" (the
// assistant reads a structured summary of the screen, made here from the page), "let me dictate" (your words go into
// the field you were in), and every assistant action (cards). Changes are still cards: say "approve" and it confirms
// out loud. It is not pixel clicking: it works through the app's own pages and actions.
//
// A small floating indicator shows it is on, what it hears as you speak, and what it did, so the page stays usable
// underneath. The conversation is saved like any other ("Voice control").
//
// Fast path (Kareem, 4 Nov): each turn runs transcript, then gate, then matcher, then assistant (voice/pipeline.ts).
// The matcher (voice/intents.ts) reads the live words as they arrive against every page and every name the person can
// see (GET /api/voice/index, loaded once and refreshed when anything changes): a confident command ("go to my
// reminders") acts a moment after its last word, without waiting for the pause, and the indicator says what it does
// ("Opening Reminders"), never Thinking. Everything else goes to the assistant as a short voice-control turn (the fast
// model). It works silently: it speaks only to answer a question or to ask a yes or no, a waiting card is shown and not
// narrated, and "approve" is confirmed with one word.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MessageResult, ProposedAction, ScreenSummary } from '@dpw/shared';
import { api } from '../api';
import { usePhone } from '../lib/media';
import { spring } from '../lib/motion';
import { Orb } from '../ui/motion';
import { StopButton } from '../assistant/VoiceBar';
import { useVoiceMode, VOICE_WORD, type TurnHooks } from './useVoiceMode';
import { NameIndex, type Decision, type Intent } from './intents';
import { OPEN_GATE, routeTurn, type Gate } from './pipeline';
import { canStream, openLive, tapPcm } from './live';
import { resumeAudioContext } from './audio';

interface VoiceControlApi { start: () => void; stop: () => void; on: boolean }
const Ctx = createContext<VoiceControlApi>({ start: () => undefined, stop: () => undefined, on: false });
export const useVoiceControl = () => useContext(Ctx);

const text = (el: Element | null | undefined) => (el?.textContent ?? '').replace(/\s+/g, ' ').trim();

/**
 * What is on the screen, as words: the page's title and path, then each section (a labelled region or panel) with
 * its first few rows, a dialog first when one is open. The assistant reads this for "what am I looking at".
 */
export function describeScreen(path: string): ScreenSummary {
  const root = document.querySelector('[role="dialog"]:not(.aip)') ?? document.querySelector('main') ?? document.body;
  const title = text(root.querySelector('h1, h2')) || document.title;
  const parts: string[] = [];
  const sections = [...root.querySelectorAll('section[aria-label], [role="region"][aria-label], [role="list"][aria-label], [role="table"][aria-label], [role="group"][aria-label]')].slice(0, 12);
  for (const s of sections) {
    const label = s.getAttribute('aria-label') ?? '';
    const rows = [...s.querySelectorAll('[role="listitem"], [role="row"], li, tr, .lrow, .ph-row, .ph-rowx, .acard, .move')].slice(0, 6).map(text).filter(Boolean);
    const body = rows.length ? rows.map(r => `- ${r.slice(0, 160)}`).join('\n') : text(s).slice(0, 300);
    if (body) parts.push(`${label}:\n${body}`);
  }
  if (!parts.length) parts.push(text(root).slice(0, 2000));
  return { path, title: title.slice(0, 200), summary: parts.join('\n\n').slice(0, 6000) };
}

/** The element that scrolls the page (the phone's main, the desktop's main column, or the document). */
function scroller(): Element {
  const cands = [document.querySelector('.ph-main'), document.querySelector('.cmain'), document.scrollingElement].filter((x): x is Element => !!x);
  return cands.find(e => e.scrollHeight > e.clientHeight + 4) ?? cands[0] ?? document.documentElement;
}

export function VoiceControlProvider({ children }: { children: ReactNode }) {
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const phone = usePhone();
  const conv = useRef<string | null>(null);
  const path = useRef(loc.pathname + loc.search);
  path.current = loc.pathname + loc.search;
  const [reply, setReply] = useState('');
  const [card, setCard] = useState<ProposedAction | null>(null);
  // What it just did on the screen ("Opening Reminders") and the words that did it.
  const [doing, setDoing] = useState<{ label: string; words: string } | null>(null);
  // The field you were in before speaking (for "let me dictate").
  const lastField = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // "Let me dictate" asked for: done once the reply has been read out and it listens again.
  const pendingDictate = useRef(false);
  useEffect(() => {
    const onFocus = (e: FocusEvent) => { const t = e.target; if ((t instanceof HTMLInputElement && /^(text|search|email|url|)$/.test(t.type)) || t instanceof HTMLTextAreaElement) lastField.current = t; };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, []);

  const act = useCallback((r: MessageResult) => {
    const m = r.reply.meta;
    if (m.nav) { nav(m.nav.to); setDoing({ label: `Opening ${m.nav.label}`, words: '' }); }
    for (const u of m.ui ?? []) {
      if (u.type === 'back') nav(-1);
      else if (u.type === 'scroll') {
        const el = scroller();
        const by = el.clientHeight * 0.8;
        el.scrollTo({ top: u.to === 'top' ? 0 : u.to === 'bottom' ? el.scrollHeight : el.scrollTop + (u.to === 'down' ? by : -by), behavior: 'smooth' });
      } else if (u.type === 'dictate') pendingDictate.current = true;
    }
    const waiting = [...r.actions].reverse().find(a => a.status === 'waiting') ?? null;
    setCard(waiting);
    if (r.approved.length) setCard(null);
    void qc.invalidateQueries();
  }, [nav, qc]);

  const say = useCallback(async (t: string, o: { spoken: true; onText: (d: string) => void }) => {
    try {
      if (!conv.current) conv.current = (await api.assistant.create({ title: 'Voice control', purpose: 'voice' })).id;
      setReply('');
      const r = await api.assistant.sendStreaming(conv.current, t, d => { setReply(x => x + d); o.onText(d); }, { spoken: true, screen: describeScreen(path.current) });
      setReply(r.reply.content);
      act(r);
      return r;
    } catch { return null; }
  }, [act]);
  // The names it matches against: what this person can see, loaded once while voice control is on, and loaded again
  // whenever anything changes (every change in the app invalidates every query).
  const names = useQuery({ queryKey: ['voice-index'], queryFn: () => api.voice.index(), enabled: false, staleTime: Infinity });
  const index = useMemo(() => (names.data ? new NameIndex(names.data.targets) : null), [names.data]);
  const indexRef = useRef(index);
  indexRef.current = index;
  // The gate (pipeline.ts): everything passes today; a wake word would go here.
  const gate = useRef<Gate>(OPEN_GATE);

  /** Do one matched command on the screen. */
  const run = useCallback((d: Extract<Decision, { kind: 'act' }>, words: string) => {
    setDoing({ label: d.label, words });
    perform(d.intent, nav, path.current);
  }, [nav]);

  // A command matched mid-sentence acts after this long without new words that change it (well under half a second).
  const SETTLE_MS = 220;
  const pending = useRef<{ label: string; timer: number } | null>(null);
  const clearPending = () => { if (pending.current) { window.clearTimeout(pending.current.timer); pending.current = null; } };
  const handledRef = useRef<() => void>(() => undefined);
  const hooks = useMemo<TurnHooks>(() => ({
    partial: words => {
      const r = routeTurn(words, { final: false, path: path.current, index: indexRef.current, gate: gate.current });
      if (r.to !== 'act' || !feasible(r.decision.intent)) { clearPending(); return; }
      if (pending.current?.label === r.decision.label) return;
      clearPending();
      const d = r.decision;
      pending.current = { label: d.label, timer: window.setTimeout(() => { pending.current = null; run(d, words); handledRef.current(); }, SETTLE_MS) };
    },
    final: words => {
      clearPending();
      const r = routeTurn(words, { final: true, path: path.current, index: indexRef.current, gate: gate.current });
      if (r.to === 'act' && feasible(r.decision.intent)) { run(r.decision, words); return { handled: true }; }
      if (r.to === 'drop') return { handled: true };
      setDoing(null);
      return { handled: false, words: r.to === 'assistant' ? r.words : words };
    },
  }), [run]);
  const voice = useVoiceMode(say, hooks);
  handledRef.current = voice.handled;
  useEffect(() => () => clearPending(), []);
  // Load the names when it starts, and again when they were marked out of date (something changed).
  useEffect(() => { if (voice.on && (!names.data || names.isStale) && !names.isFetching && !names.isError) void names.refetch(); }, [voice.on, names]);
  // New words replace what it last did on the indicator.
  useEffect(() => { if (voice.heard) setDoing(null); }, [voice.heard]);

  // "Let me dictate": voice control pauses, and your words go into the field you were in, live, until a pause.
  const [dictating, setDictating] = useState(false);
  useEffect(() => {
    if (!pendingDictate.current || voice.state !== 'listening') return;
    pendingDictate.current = false;
    const field = lastField.current;
    if (!field || !document.contains(field) || !canStream()) return;
    voice.end();
    setDictating(true);
    void dictateInto(field).finally(() => { setDictating(false); voice.start(); });
  }, [voice.state, voice]);

  const start = useCallback(() => { setReply(''); setCard(null); setDoing(null); conv.current = null; voice.start(); }, [voice]);
  const stop = useCallback(() => { voice.end(); setReply(''); setCard(null); setDoing(null); }, [voice]);
  const value = useMemo(() => ({ start, stop, on: voice.on }), [start, stop, voice.on]);
  const live = voice.heard;
  const line = dictating ? 'Writing into the field. Pause to finish.' : live ? live : doing && voice.state === 'listening' ? doing.words || 'Say where to go or what to do.'
    : voice.state === 'listening' ? 'Say where to go or what to do.' : reply || VOICE_WORD[voice.state];
  const word = dictating ? 'Dictating' : doing && (voice.state === 'listening' || voice.state === 'hearing') && !live ? doing.label : VOICE_WORD[voice.state];

  return (
    <Ctx.Provider value={value}>
      {children}
      <AnimatePresence>
        {(voice.on || dictating) && (
          <motion.div key="vctl" className={`vctl-host${phone ? ' phone' : ''}`}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }} exit={{ opacity: 0, y: 16 }}>
          <div className="vctl" role="region" aria-label="Voice control">
            <button type="button" className="vorb" aria-label={voice.state === 'speaking' ? 'Interrupt' : 'Voice control'} onClick={() => (voice.state === 'speaking' ? voice.interrupt() : voice.state === 'blocked' ? voice.tapToHear() : undefined)}>
              <Orb size={26} state={voice.state === 'thinking' ? 'thinking' : voice.state === 'listening' || voice.state === 'hearing' ? 'listening' : 'idle'} level={voice.level} />
            </button>
            <div className="vt" aria-live="polite">
              <b data-doing={doing && word === doing.label ? '1' : undefined}>{word}{card && !dictating ? <span className="vcard"> · {card.kindLabel} waiting, say approve</span> : null}</b>
              <span className={live ? 'live' : ''}>{voice.error || line}</span>
            </div>
            {voice.state === 'blocked' && <button type="button" className="bt pri" onClick={voice.tapToHear}>Tap to hear</button>}
            <StopButton label="End voice control" onClick={() => { voice.end(); setReply(''); setCard(null); setDoing(null); }} />
          </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
}

/** A visible button whose words are these (an "add" button: "New activity", "Add reminder"). */
function findButton(re: RegExp): HTMLButtonElement | null {
  const all = [...document.querySelectorAll<HTMLButtonElement>('[role="dialog"] button, main button, .cmain button')];
  return all.find(b => !b.disabled && b.offsetParent !== null && re.test(text(b).replace(/^\+\s*/, ''))) ?? null;
}
const FORM_BUTTON: Record<Extract<Intent, { type: 'new' }>['what'], RegExp> = {
  activity: /^New activity$/, track: /^New track$/, reminder: /^Add reminder$/, folder: /^New folder$/, insight: /^Add an insight$/, research: /^New research task$/,
};
/** Where each form's button lives, when it is not on this page. Activities and tracks belong to the page you are on. */
const FORM_PAGE: Partial<Record<Extract<Intent, { type: 'new' }>['what'], string>> = { reminder: '/reminders', folder: '/folders', insight: '/knowledge', research: '/knowledge' };

/** Can this be done here? "New activity" needs a page with that button (a space or a track); else the assistant takes it. */
function feasible(i: Intent): boolean {
  if (i.type !== 'new') return true;
  return !!FORM_PAGE[i.what] || !!findButton(FORM_BUTTON[i.what]);
}

const waitFor = <T,>(get: () => T | null, ms: number) => new Promise<T | null>(resolve => {
  const end = Date.now() + ms;
  const tick = () => { const v = get(); if (v || Date.now() > end) resolve(v); else window.setTimeout(tick, 50); };
  tick();
});

/** Do a command with the app's own pages and buttons (never pixel clicks). */
function perform(i: Intent, nav: ReturnType<typeof useNavigate>, path: string) {
  switch (i.type) {
    case 'open': if (i.target.path !== path) nav(i.target.path); return;
    case 'search': nav(`/search?q=${encodeURIComponent(i.q)}`); return;
    case 'back': nav(-1); return;
    case 'scroll': {
      const el = scroller();
      const by = el.clientHeight * 0.8;
      el.scrollTo({ top: i.to === 'top' ? 0 : i.to === 'bottom' ? el.scrollHeight : el.scrollTop + (i.to === 'down' ? by : -by), behavior: 'smooth' });
      return;
    }
    case 'close': document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })); return;
    case 'filter': nav(`${path.split('?')[0]}?${new URLSearchParams(i.params).toString()}`); return;
    case 'new': {
      const page = FORM_PAGE[i.what];
      if (page && !findButton(FORM_BUTTON[i.what]) && !path.startsWith(page)) nav(page);
      void waitFor(() => findButton(FORM_BUTTON[i.what]), 2500).then(b => {
        b?.click();
        // "New meeting": the form opens on that type.
        if (b && i.activityType) {
          void waitFor(() => document.querySelector<HTMLButtonElement>(`[role="dialog"] [role="radiogroup"][aria-label="Type"] button:nth-child(${['meeting', 'workshop', 'deliverable', 'task'].indexOf(i.activityType!) + 1})`), 1500)
            .then(t => t?.click());
        }
      });
      return;
    }
  }
}

/** Live dictation into one field: its value grows as you speak (a React-controlled field gets real input events). */
async function dictateInto(field: HTMLInputElement | HTMLTextAreaElement): Promise<void> {
  resumeAudioContext();
  const proto = field instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setValue = Object.getOwnPropertyDescriptor(proto, 'value')!.set!;
  const base = field.value.trim();
  const put = (said: string) => { setValue.call(field, base ? `${base} ${said}` : said); field.dispatchEvent(new Event('input', { bubbles: true })); };
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  field.focus();
  await new Promise<void>(resolve => {
    const finals: string[] = [];
    let stopTap: (() => void) | null = null;
    const done = () => { stopTap?.(); stream.getTracks().forEach(t => t.stop()); link.close(); window.clearTimeout(cap); resolve(); };
    const link = openLive({ mode: 'dictate', turn: 0 }, {
      onPartial: t => put([...finals, t].filter(Boolean).join(' ')),
      onFinal: t => { finals.push(t); put(finals.join(' ')); done(); },
      onError: () => done(),
      onEnd: () => done(),
    });
    const cap = window.setTimeout(() => { void link.commit().then(done); }, 60_000);
    void tapPcm(stream, b => link.send(b)).then(s => { stopTap = s; }, () => done());
  });
}
